'use client';

import { css } from '@repo/pandacss/styled-system/css';
import { useAnalytics } from '@repo/segment/Analytics';
import { FeatureViewed } from '@repo/segment/components/FeatureViewed';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { v4 as uuid } from 'uuid';
import { useClientOnlyConstant } from '@repo/segment/hooks/useClientOnlyConstant';
import { PlayMediaIcon } from './PlayMediaIcon';
import type { WPVideoProps } from './types';
import { getVideoImageSRC, getVideoImageSRCSet, getVideoUrl } from './utils';
import { VideoLinkedData } from './VideoLinkedData';

export function WPVideo({ props }: WPVideoProps) {
  const {
    options = { showPreview: true, showRelatedVideos: false },
    video,
    shouldRenderJsonLd = true,
  } = props;
  const [showPreview, setShowPreview] = useState(options.showPreview ?? true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const intervalRef = useRef<NodeJS.Timeout>(undefined);
  const eventFiredRef = useRef<boolean>(false);
  const impressionId = useClientOnlyConstant(() => uuid(), '');

  const { analytics, deployableName, gitSha } = useAnalytics();

  if (!video || !video.youtubeVideo) return null;

  const segmentInstance = analytics?.segmentInstance;
  const videoId = video?.youtubeVideo?.id;

  let timeLength = options.timeLength;
  if (!timeLength?.match(/^(([0]?[0-5][0-9]|[0-9]):([0-5][0-9]))$/)) {
    timeLength = undefined;
  }

  const fireInteraction = () => {
    eventFiredRef.current = true;

    if (segmentInstance) {
      segmentInstance.track(
        'Feature Interacted',
        {
          entity_name: 'video',
          section_name: null,
          item_position: null,
          item_copy: null,
          event_id: uuid(),
          content_id: videoId,
          parent_event_id: impressionId,
          additional_attributes: [],
        },
        {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        },
      );
    }
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (eventFiredRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
        return;
      }
      if (!document?.activeElement) return;
      if (document.activeElement === iframeRef.current) {
        fireInteraction();
      }
    }, 250);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <FeatureViewed
      entityName="video"
      additionalAttributes={{
        content_id: videoId,
        event_id: impressionId,
      }}
    >
      <div
        className={css({
          position: 'relative',
          height: '0',
          paddingBottom: '[56.25%]',
          overflowX: 'hidden',
          overflowY: 'hidden',
        })}
      >
        {showPreview ? (
          <div
            className={css({
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center',
              overflowX: 'hidden',
              overflowY: 'hidden',
              position: 'relative',
              alignItems: 'center',
            })}
            onClick={() => {
              setShowPreview(false);
              fireInteraction();
            }}
            onKeyUp={() => {
              setShowPreview(false);
              fireInteraction();
            }}
            role="button"
            tabIndex={0}
          >
            {/* Play button circle — 70px mobile, 96px desktop */}
            <div
              className={css({
                backgroundColor: 'white',
                cursor: 'pointer',
                opacity: '0.9',
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderTopLeftRadius: '[100px]',
                borderTopRightRadius: '[100px]',
                borderBottomRightRadius: '[100px]',
                borderBottomLeftRadius: '[100px]',
                zIndex: '1',
                borderTopWidth: 'none',
                borderRightWidth: 'none',
                borderBottomWidth: 'none',
                borderLeftWidth: 'none',
                borderTopStyle: 'none',
                borderRightStyle: 'none',
                borderBottomStyle: 'none',
                borderLeftStyle: 'none',
                width: {
                  base: '[70px]',
                  sm: '24',
                },
                height: {
                  base: '[70px]',
                  sm: '24',
                },
              })}
              aria-label="Play"
              role="button"
            >
              <PlayMediaIcon
                color="#005FB9"
                size="44px"
                style={
                  {
                    marginLeft: '4px',
                    marginTop: '7px',
                  } as CSSProperties
                }
                className={css({
                  height: {
                    sm: '14',
                  },
                  width: {
                    sm: '14',
                  },
                  marginTop: {
                    sm: '0.5',
                  },
                })}
              />
            </div>
            {timeLength && (
              <div
                className={css({
                  backgroundColor: 'black',
                  textAlign: 'center',
                  position: 'absolute',
                  paddingInlineStart: '4',
                  paddingInlineEnd: '4',
                  paddingBlockStart: '2',
                  paddingBlockEnd: '2',
                  zIndex: '1',
                  bottom: '4',
                  right: '4',
                  sm: {
                    bottom: '6',
                    right: '6',
                  },
                })}
              >
                <span className={css({ color: 'white' })}>{timeLength}</span>
              </div>
            )}
            <img
              alt="Video preview image"
              fetchPriority="high"
              className={css({ objectFit: 'cover', width: 'full', height: 'full' })}
              src={getVideoImageSRC(video)}
              srcSet={getVideoImageSRCSet(video)}
            />
          </div>
        ) : (
          <iframe
            title={video.youtubeVideo.snippet.title}
            ref={iframeRef}
            src={getVideoUrl(videoId, options)}
            width="100%"
            height="100%"
            style={{ border: 0, top: 0, left: 0, position: 'absolute' }}
            allow="autoplay"
            allowFullScreen
          />
        )}
        {shouldRenderJsonLd && <VideoLinkedData id="video-linked-data" video={video} />}
      </div>
    </FeatureViewed>
  );
}
